import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { BadgeDefinition } from '@/services/achievementService';

interface BadgeNotificationProps {
  badge: BadgeDefinition;
  message: string;
  onClose: () => void;
}

export const BadgeNotification: React.FC<BadgeNotificationProps> = ({
  badge,
  message,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for animation to complete
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`fixed top-4 right-4 z-50 transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <Card className="w-80 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center text-2xl border-2 border-yellow-300">
                {badge.icon}
              </div>
              <div>
                <h3 className="font-semibold text-sm text-gray-900">
                  🎉 Badge Earned!
                </h3>
                <p className="text-sm text-gray-700">{message}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsVisible(false);
                setTimeout(onClose, 300);
              }}
              className="h-6 w-6 p-0 hover:bg-yellow-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const BadgeNotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [notification, setNotification] = useState<{
    badge: BadgeDefinition;
    message: string;
  } | null>(null);

  useEffect(() => {
    const handleBadgeEarned = (event: CustomEvent) => {
      setNotification(event.detail);
    };

    window.addEventListener('badge-earned', handleBadgeEarned as EventListener);
    return () => {
      window.removeEventListener('badge-earned', handleBadgeEarned as EventListener);
    };
  }, []);

  return (
    <>
      {children}
      {notification && (
        <BadgeNotification
          badge={notification.badge}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </>
  );
};
